import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { Translate } from 'react-localize-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import styled, { useTheme } from 'styled-components';

import AppState from '@webapp/store/state';
import { UPDATE_ARTIST } from '@webapp/store/artist/actions';
import { UpdateArtist } from '@webapp/store/artist/action-creators';

import SimpleArtist from '@common/types/artist/simple-artist';
import { ReleaseListSummary } from '@common/types/release/release-list-summary';

import {
  validateArtistsPlatforms,
  validatePlatform,
  validateSpotifyUrl,
} from '@webapp/utils/validators/artist';

import { NewInstance } from '@common/types/data/new-instance';
import { ArtistPlatforms } from '@common/types/artist/artist-platforms';
import { StreamingPlatform } from '@common/types/data/streaming-platform';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import Hint from '@components/basics/hint';
import { getWidth } from '@components/styles';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import { ErrorMessage } from '@components/basics/error-message';

import ButtonLoadingCtrl from '@webapp/controllers/button-loading';
import StreamingPlatformCtrl from '@webapp/controllers/form/streaming-platform-list';

import CatalogReleaseInfoSidebar from '../../catalog/release-info-sidebar';
import { updatedArtistSideEffect } from '@webapp/utils/artists/update-artist-rudderstack';
import TracPage from '@services/tracpage/types/trac-page';
import { TracPageSocialLink } from '@services/tracpage/types/track-page-social-link';

interface CorrectArtistProfileInfoPageProps {
  className?: string;
  artist?: SimpleArtist;
  artistTracPage: TracPage;
  social: TracPageSocialLink[];
  isLoading: boolean;
  updateArtist: (artistData: SimpleArtist) => UpdateArtist;
  isMobile: boolean;
  summary: Partial<ReleaseListSummary>;
  push: (path: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  artist: state.artist.active,
  social: state.tracpage.artistPage.data.social,
  artistTracPage: state.tracpage.artistPage,
  summary: state.release.summary,
  isMobile: state.ui.mobile,
  isLoading: !!state.ui.loading[UPDATE_ARTIST],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  push: (path: string) => dispatch(push(path)),
  updateArtist: (artistData: SimpleArtist) =>
    dispatch(new UpdateArtist(artistData)),
});

const validateArtist = (artistData?: SimpleArtist) => {
  return (
    artistData &&
    artistData.name &&
    validateArtistsPlatforms(artistData.platforms)
  );
};
export const ONBOARDING_ARTIST_PLATFORMS: ArtistPlatforms[] = [
  'spotify',
  'apple',
  'soundcloud',
];
export const getStreamingPlatforms = (
  platforms: SimpleArtist['platforms'],
  showErrors: boolean = true
): StreamingPlatform[] => {
  return ONBOARDING_ARTIST_PLATFORMS.map((platform) => {
    const handle = (platforms ? platforms[platform] : '') || '';

    let error: JSX.Element | null = null;

    if (!validatePlatform(handle)) {
      error = (
        <MotionOpacity>
          <ErrorMessage
            className="mb-5"
            errorId="ONBOARDING.ARTIST.ERROR.URL"
          />
        </MotionOpacity>
      );
    }

    if (platform === 'spotify' && !handle.length) {
      error = (
        <MotionOpacity>
          <ErrorMessage
            className="mb-5"
            errorId="ONBOARDING.ARTIST.ERROR.STREAMING"
          />
        </MotionOpacity>
      );
    }

    if (
      platform === 'spotify' &&
      handle.length &&
      !validateSpotifyUrl(handle)
    ) {
      error = (
        <MotionOpacity>
          <ErrorMessage
            className="mb-5"
            errorId="ONBOARDING.ARTIST.ERROR.SPOTIFY_URL"
          />
        </MotionOpacity>
      );
    }

    return {
      platform,
      handle: handle,
      errorMessage: showErrors ? error : null,
    };
  });
};

export const CorrectArtistProfileInfoPage: React.FC<
  CorrectArtistProfileInfoPageProps
> = (props) => {
  const theme = useTheme();

  const { artist, summary } = props;
  const match = useRouteMatch<{ releaseId: string }>();
  const releaseId = match.params.releaseId;
  const [artistData, setArtistData] = React.useState(artist);
  const [disabled, setDisabled] = React.useState(false);
  const [releaseTitle, setReleaseTitle] = React.useState<string>('');
  const [releaseCover, setReleaseCover] = React.useState<string>('');
  const [platforms, setPlatforms] = React.useState<SimpleArtist['platforms']>(
    artistData?.platforms || {}
  );
  React.useEffect(() => {
    if (summary?.all?.list) {
      summary.all.list.forEach((release) =>
        release.id === releaseId
          ? (setReleaseTitle(release.title), setReleaseCover(release.cover.url))
          : null
      );
    }
  }, [releaseId, summary]);
  React.useEffect(() => setPlatforms(artistData?.platforms), [artistData]);
  React.useEffect(() => setArtistData(artist), [artist]);

  React.useEffect(() => {
    setDisabled(!validateArtist(artistData));
  }, [artistData, disabled]);

  if (!(artist && artistData)) {
    return null;
  }

  const saveChanges = () => {
    if (validateArtist(artistData)) {
      const externalIds = {
        apple: artistData.platforms?.apple
          ? undefined
          : artistData.externalIds?.apple,
        soundcloud: artistData.platforms?.soundcloud
          ? undefined
          : artistData.externalIds?.soundcloud,
        spotify: artistData.platforms?.spotify
          ? undefined
          : artistData.externalIds?.spotify,
      };
      const newArtistData: SimpleArtist = {
        ...artistData,
        externalIds: externalIds,
      };
      props.updateArtist(newArtistData);
      updatedArtistSideEffect({
        artistData: newArtistData,
        artistTracPage: props.artistTracPage,
        social: props.social,
      });
    }
  };
  const handlePlatformUpdate = (newPlatforms: StreamingPlatform[]) => {
    const update = newPlatforms.reduce(
      (memo, p) => ({
        ...memo,
        [p.platform]: p.handle || '',
      }),
      {} as SimpleArtist['platforms']
    );
    setPlatforms(update);
    handleChange({ platforms: update });
  };

  const handleChange = (newVal?: Partial<NewInstance<SimpleArtist>>) => {
    const newArtist: NewInstance<SimpleArtist> = {
      // @ts-ignore
      type: 'solo' as ArtistType,
      ...(newVal as NewInstance<SimpleArtist>),
    };

    if (newArtist) {
      return setArtistData({ ...artistData, ...newArtist });
    }
  };

  return (
    <MotionOpacity className={props.className}>
      <CorrectProfileWrapper>
        <CatalogReleaseInfoSidebar
          releaseTitle={releaseTitle}
          releaseCover={releaseCover}
        />

        <Header gap={10}>
          <Typography customFont="semi-bold" type="subheader">
            <Translate id="ARTIST_PROFILE.CORRECT.TITLE" />
          </Typography>
          <Typography color={theme.colors.tertiary}>
            <Translate id="ARTIST_PROFILE.CORRECT.SUBTITLE" />
          </Typography>
        </Header>

        <Box noBorderBottom>
          <StreamingPlatformCtrl
            disableSelect
            filterKeys={[]}
            title={<Translate id="ARTIST_PROFILE.CORRECT.BOX.TITLE" />}
            text=" "
            platforms={getStreamingPlatforms(platforms, true)}
            onChange={(newPlatforms) => {
              if (newPlatforms) {
                handlePlatformUpdate(newPlatforms);
              } else {
                setPlatforms(undefined);
                handleChange({ platforms: undefined });
              }
            }}
          />
        </Box>
        <Hint
          noBorderTop
          customTitle={<Translate id="ARTIST_PROFILE.CORRECT.HINT.TITLE" />}
        >
          <Translate id="ARTIST_PROFILE.CORRECT.HINT.TEXT" />
        </Hint>

        <ButtonWrapper>
          <Button
            className="btn"
            buttonType="secondary"
            onClick={() => props.push('/catalog')}
          >
            <Translate id="ARTIST_PROFILE.CORRECT.BUTTON.CANCEL" />
          </Button>
          <ButtonLoadingCtrl
            className="btn"
            isLoading={!!props.isLoading}
            onClick={() => saveChanges()}
            disabled={disabled}
          >
            <Translate id="ARTIST_PROFILE.CORRECT.BUTTON.SAVE" />
          </ButtonLoadingCtrl>
        </ButtonWrapper>
      </CorrectProfileWrapper>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(CorrectArtistProfileInfoPage)`
  position: relative;
  margin-top: 80px;
`);

const CorrectProfileWrapper = styled.div`
  min-height: 100vh;
  padding-inline: 24px;
  max-width: ${getWidth(10)};
  margin-inline: auto;

  @media ${({ theme }) => theme.device.laptopS} {
    width: auto;
    padding-inline: 0;
  }

  .title-header {
    margin-bottom: 20px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 40px;
  gap: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .btn {
    width: 100%;
    flex-grow: 1;
  }
`;

const Header = styled(Grid)`
  margin-bottom: 30px;
`;
